import Phaser from 'phaser';
import { COLORS } from '../theme';

/**
 * Phaser has no native radial gradient, so we bake one into a 256×256 canvas
 * texture once per (key, color, peak alpha) tuple, then reuse it as a single
 * image. Previous revision stacked 6–8 translucent ellipses which left visible
 * ring banding ("onion layers") — this gives a clean falloff and keeps the
 * GPU happy (one quad instead of eight).
 */
function ensureRadialTexture(
  scene: Phaser.Scene,
  key: string,
  color: number,
  peakAlpha: number,
): void {
  if (scene.textures.exists(key)) return;
  const size = 256;
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const cv = scene.textures.createCanvas(key, size, size);
  if (!cv) return;
  const ctx = cv.getContext();
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, `rgba(${r},${g},${b},${peakAlpha})`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  cv.refresh();
}

/** Soft yolk radial spotlight behind the character. */
export function drawSpotlight(
  scene: Phaser.Scene,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): Phaser.GameObjects.Container {
  ensureRadialTexture(scene, 'tex_spotlight_yolk', COLORS.yolk, 0.55);
  const img = scene.add.image(0, 0, 'tex_spotlight_yolk');
  img.setDisplaySize(rx * 2, ry * 2);
  const container = scene.add.container(cx, cy);
  container.setDepth(3);
  container.add(img);
  return container;
}

/** Hype radial flash — coral haze behind dog, slow yoyo to give the scene a pulse. */
export function drawHypeFlash(
  scene: Phaser.Scene,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): Phaser.GameObjects.Container {
  ensureRadialTexture(scene, 'tex_hypeflash_coral', COLORS.coral, 0.45);
  const img = scene.add.image(0, 0, 'tex_hypeflash_coral');
  img.setDisplaySize(rx * 2, ry * 2);
  const container = scene.add.container(cx, cy);
  container.setDepth(2);
  container.add(img);
  scene.tweens.add({
    targets: container,
    alpha: 0.55,
    duration: 1800,
    ease: 'Sine.InOut',
    yoyo: true,
    repeat: -1,
  });
  return container;
}
